import { Box, Button, Divider, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { Message } from "../../../../../api-types/job-requests/common";
import { Section } from "../../../../common/components";
import { useProfessionalReferralQuery } from "../../../../common/hooks/projects/project/collaborations/useProfessionalReferralQuery";
import { useScopedDowngradedStateValue } from "../../../../common/hooks/utils/useScopedDowngradedStateValue";
import { useUser } from "../../../../state/hooks";
import { MessagesForm } from "../../../bids/communication/MessagesForm";
import { useAccountInquiryQuery } from "../../hooks/useAccountInquiry";
import { useAddInquiryMessageMutations } from "../../hooks/useAddInquiryMessageMutation";
import { useBidByJobRequestId } from "../../hooks/useBidByProjectId";
import { useCreateInquiryMutation } from "../../hooks/useCreateInquiryMutation";
import { useInquiryByJobRequestId } from "../../hooks/useInquiryByProjectId";
import { BidSection } from "./BidSection";

type CommunicationProps = {};

export const Communication: React.FC<CommunicationProps> = () => {
  const {
    data: professionalReferral,
    isLoading: isLoadingProfessionalReferral,
  } = useProfessionalReferralQuery();

  const [placeBid, setPlaceBid] = useState(false);

  const jobRequestId = professionalReferral?.project?.jobRequest?._id;

  const accountInquiryId = useInquiryByJobRequestId(jobRequestId);

  const { data: inquiryData, isLoading: isLoadingInquiry } =
    useAccountInquiryQuery(accountInquiryId);

  const { id, accountType } = useScopedDowngradedStateValue(useUser());

  const { mutate: addInquiryMessage, isLoading: isAddingNew } =
    useAddInquiryMessageMutations(undefined, accountInquiryId);

  const { mutate: createInquiry, isLoading: isCreating } =
    useCreateInquiryMutation();

  const handlePlaceBid = useCallback(() => {
    setPlaceBid(true);
  }, []);

  const handleCancelBid = useCallback(() => {
    setPlaceBid(false);
  }, []);

  const onNewMessageHandler = (newMessage: string) => {
    if (isLoadingInquiry) return;

    const messages: Message[] = [
      {
        content: newMessage,
        from: id ?? "",
        to: id ?? "",
      },
    ];

    if (!accountInquiryId && jobRequestId) {
      createInquiry({ jobRequestId: jobRequestId, messages });
    } else {
      addInquiryMessage({
        messages,
        accountType,
        jobRequestId: inquiryData?.jobRequest._id,
      });
    }
  };

  const messages = useMemo(() => {
    if (isLoadingInquiry || !inquiryData) return [];

    return inquiryData.messages;
  }, [inquiryData, isLoadingInquiry]);

  const accountBidId = useBidByJobRequestId(jobRequestId);

  const hasPlaceBidButton = useMemo(
    () => !placeBid && !accountBidId,
    [accountBidId, placeBid]
  );

  const hasBid = useMemo(() => {
    if (accountBidId) {
      handleCancelBid();
    }
    return placeBid || accountBidId;
  }, [accountBidId, handleCancelBid, placeBid]);

  const isLoading = isCreating || isAddingNew || isLoadingInquiry;

  return (
    <Section
      isLoading={isLoadingInquiry || isLoadingProfessionalReferral}
      sectionProps={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      {hasBid ? (
        <BidSection editing={placeBid} onCancel={handleCancelBid} />
      ) : (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-end"
            columnGap={2}
          >
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 600,
              }}
            >
              Inquiries and Replies
            </Typography>
            {hasPlaceBidButton && (
              <Button size="small" variant="contained" onClick={handlePlaceBid}>
                Place bid
              </Button>
            )}
          </Box>
          <Box py={2.5}>
            <Divider />
          </Box>
          <MessagesForm
            height={250}
            messages={messages}
            onNewMessage={onNewMessageHandler}
            isLoading={isLoading}
          />
        </>
      )}
    </Section>
  );
};
